import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect, Suspense } from 'react'
import { apiClient } from '@/lib/api'
import { useInterviewStore, InterviewStage, type SystemDesignPrompt } from '@/store/interviewStore'
import { useNavigationWithSave } from '@/hooks/useNavigationWithSave'
import CodingChallenge from '@/components/interview/CodingChallenge'
import DatabaseQuestions from '@/components/interview/DatabaseQuestions'
import WhiteboardDiscussion from '@/components/interview/WhiteboardDiscussion'
import SystemDesign from '@/components/interview/SystemDesign'
import DataModeling from '@/components/interview/DataModeling'
import UMLDesign from '@/components/interview/UMLDesign'
import NotesStage from '@/components/interview/NotesStage'
import Notepad from '@/components/interview/Notepad'
import { buildApiUrl } from '@/config/api'
import { Clock, Code2, Palette, FileText, Layers, ChevronLeft, ChevronRight, StickyNote, Database, Table, Boxes } from 'lucide-react'

// Loading component for Suspense fallback
function InterviewLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading interview session...</p>
      </div>
    </div>
  )
}

const DEFAULT_SYSTEM_DESIGN_PROMPT = {
  title: 'Design a social media platform like Twitter',
  description:
    'The system should support millions of users posting short messages (tweets), following other users, and viewing a personalized timeline. Consider scalability, availability, and consistency requirements.',
  requirements: [
    '100M daily active users',
    'Users can post tweets (280 chars)',
    'Users can follow other users',
    'Real-time timeline updates',
    'Search functionality'
  ],
  considerations: [
    'Database partitioning strategy',
    'Caching mechanisms',
    'Message queues',
    'CDN for media content',
    'Rate limiting',
    'Data consistency'
  ]
}

const toTrimmedArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item.trim()
        if (item === null || item === undefined) return ''
        return String(item).trim()
      })
      .filter(Boolean)
  }
  return []
}

const normalizeSystemDesignPrompt = (row: any): SystemDesignPrompt => {
  const rawNotes = typeof row?.notes === 'string'
    ? row.notes
    : row?.notes
    ? JSON.stringify(row.notes)
    : null

  const parsedCandidate = row?.parsed_notes || row?.parsedNotes
  let parsedNotes = null

  if (parsedCandidate && typeof parsedCandidate === 'object') {
    parsedNotes = {
      title:
        typeof parsedCandidate.title === 'string'
          ? parsedCandidate.title
          : typeof parsedCandidate.name === 'string'
          ? parsedCandidate.name
          : undefined,
      description:
        typeof parsedCandidate.description === 'string'
          ? parsedCandidate.description
          : typeof parsedCandidate.prompt === 'string'
          ? parsedCandidate.prompt
          : rawNotes || undefined,
      requirements: toTrimmedArray(parsedCandidate.requirements),
      considerations: toTrimmedArray(parsedCandidate.considerations),
      additionalNotes:
        typeof parsedCandidate.additionalNotes === 'string'
          ? parsedCandidate.additionalNotes
          : undefined
    }
  } else if (rawNotes?.trim()) {
    parsedNotes = {
      description: rawNotes.trim()
    }
  }

  const interviewer = row?.interviewer || {}

  return {
    uuid: row?.uuid ?? null,
    role: row?.role ?? null,
    status: row?.status ?? null,
    notes: rawNotes,
    parsedNotes,
    assignedAt: row?.assigned_at ?? row?.assignedAt ?? null,
    acceptedAt: row?.accepted_at ?? row?.acceptedAt ?? null,
    completedAt: row?.completed_at ?? row?.completedAt ?? null,
    interviewer: interviewer
      ? {
          id: interviewer.id ?? interviewer.interviewer_id ?? null,
          uuid: interviewer.uuid ?? interviewer.interviewer_uuid ?? null,
          firstName: interviewer.first_name ?? interviewer.firstName ?? null,
          lastName: interviewer.last_name ?? interviewer.lastName ?? null,
          email: interviewer.email ?? null,
          title: interviewer.title ?? null
        }
      : null
  }
}

const parseJsonSafely = <T = unknown>(value: unknown): T | unknown => {
  if (typeof value !== 'string') {
    return value
  }

  const trimmed = value.trim()
  if (!trimmed) {
    return value
  }

  try {
    return JSON.parse(trimmed) as T
  } catch {
    return value
  }
}

const toObject = (value: unknown): Record<string, any> | null => {
  const parsed = parseJsonSafely<Record<string, any>>(value)
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
    ? (parsed as Record<string, any>)
    : null
}

const toArray = <T = any>(value: unknown): T[] | null => {
  const parsed = parseJsonSafely<T[]>(value)
  return Array.isArray(parsed) ? parsed as T[] : null
}

const pickArray = <T = any>(...sources: Array<T[] | undefined | null>): T[] => {
  for (const source of sources) {
    if (Array.isArray(source) && source.length > 0) {
      return source as T[]
    }
  }
  return []
}

const toOptionalString = (value: unknown, fallback?: string): string | undefined => {
  if (value === undefined || value === null) return fallback
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length ? trimmed : fallback
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  try {
    return String(value)
  } catch {
    return fallback
  }
}

const parseNumber = (value: unknown, fallback?: number): number | undefined => {
  if (value === undefined || value === null || value === '') return fallback
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback
  }
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }
  return fallback
}

const parseBoolean = (value: unknown, defaultValue = false): boolean => {
  if (value === undefined || value === null) return defaultValue
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) return true
    if (['false', '0', 'no', 'n', 'off'].includes(normalized)) return false
  }
  return defaultValue
}

// Main interview component that uses useSearchParams
function InterviewContent() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const store = useInterviewStore()
  const { activeStage, switchStage } = useNavigationWithSave()
  
  // Get session parameters from URL
  const sessionIdFromUrl = searchParams.get('sessionId') || searchParams.get('session')
  const interviewIdFromUrl = searchParams.get('interviewId') || '1'
  const stageFromUrl = searchParams.get('stage')
  
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null)
  const [sessionDurationSeconds, setSessionDurationSeconds] = useState<number>(3600)
  const [sessionStartMs, setSessionStartMs] = useState<number | null>(null)
  const [isExpired, setIsExpired] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [isNotepadOpen, setIsNotepadOpen] = useState(false)
  const [interviewConfig, setInterviewConfig] = useState({
    guidelinesRead: false
  })
  const [stageTimers, setStageTimers] = useState<Record<InterviewStage, number>>({})
  const storageKey = sessionIdFromUrl ? `interview-timers-${sessionIdFromUrl}` : null

  // State to track if data has been loaded
  const [dataLoaded, setDataLoaded] = useState(false)
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isAuthLoading, setIsAuthLoading] = useState(true)
  
  // State to track available challenges for smart stage mapping
  const [availableChallenges, setAvailableChallenges] = useState({
    coding: false,
    database: false,
    loaded: false
  })

  const loadPersistedTiming = () => {
    if (!storageKey) return null
    try {
      const raw = localStorage.getItem(storageKey)
      if (!raw) return null
      return JSON.parse(raw)
    } catch (e) {
      console.warn('Failed to parse persisted timers', e)
      return null
    }
  }

  const persistTiming = (timers: Record<InterviewStage, number>, options?: { lastActiveStage?: InterviewStage | null }) => {
    if (!storageKey) return
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        stageTimers: timers,
        sessionStartMs,
        sessionDurationSeconds,
        lastTickMs: Date.now(),
        lastActiveStage: options?.lastActiveStage ?? activeStage
      }))
    } catch (e) {
      console.warn('Failed to persist timers', e)
    }
  }

  const persistTimersToServer = async () => {
    if (!sessionIdFromUrl || isExpired) return
    try {
      await fetch(buildApiUrl('interview-sessions/public/save-timers'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionIdFromUrl,
          interviewId: interviewIdFromUrl,
          remainingSeconds: remainingSeconds ?? sessionDurationSeconds,
          sessionDurationSeconds,
          stageTimers,
          activeStage
        })
      })
    } catch (err) {
      console.error('Failed to persist timers to server', err)
    }
  }

  const extractChallengeList = (payload: unknown): any[] => {
    if (Array.isArray(payload)) {
      return payload
    }
    if (payload && typeof payload === 'object') {
      const dataCandidate = (payload as Record<string, unknown>).data
      if (Array.isArray(dataCandidate)) {
        return dataCandidate
      }
      const challengeCandidate = (payload as Record<string, unknown>).challenges
      if (Array.isArray(challengeCandidate)) {
        return challengeCandidate
      }
    }
    return []
  }

  // Authentication check - for candidate interviews, no login required
  useEffect(() => {
    const checkAuthentication = () => {
      // For candidate interview page, no authentication required
      // Candidates access interviews via unique session URLs
      setIsAuthenticated(true)
      setIsAuthLoading(false)
    }

    checkAuthentication()
  }, [navigate])

  // Load available challenges to determine what stages are available
  useEffect(() => {
    const loadAvailableChallenges = async () => {
      if (!isAuthenticated || !interviewIdFromUrl) return
      
      try {
        console.log('🔍 Checking available challenges for interview:', interviewIdFromUrl)
        
        // Check for database challenges
        const dbResponse = await apiClient.getInterviewDatabaseChallenges(interviewIdFromUrl)
        const dbChallenges = extractChallengeList(dbResponse.data)
        const hasDatabaseChallenges = dbResponse.success && dbChallenges.length > 0
        
        // Check for coding challenges  
        const codingResponse = await apiClient.getInterviewCodingChallenges(interviewIdFromUrl)
        const codingChallenges = extractChallengeList(codingResponse.data)
        const hasCodingChallenges = codingResponse.success && codingChallenges.length > 0
        
        console.log('📊 Available challenges:', {
          database: hasDatabaseChallenges,
          coding: hasCodingChallenges,
          dbCount: dbChallenges.length,
          codingCount: codingChallenges.length
        })
        
        setAvailableChallenges({
          coding: hasCodingChallenges,
          database: hasDatabaseChallenges,
          loaded: true
        })
      } catch (error) {
        console.error('❌ Error checking available challenges:', error)
        setAvailableChallenges({
          coding: false,
          database: false,
          loaded: true
        })
      }
    }
    
    loadAvailableChallenges()
  }, [isAuthenticated, interviewIdFromUrl, sessionIdFromUrl])

  // Load session metadata (duration, start time)
  useEffect(() => {
    const loadSessionMetadata = async () => {
      if (!sessionIdFromUrl) return

      try {
        const response = await fetch(buildApiUrl(`interview-sessions/public/${sessionIdFromUrl}`), {
          method: 'GET'
        })
        const data = await response.json()
        const persisted = loadPersistedTiming()
        if (response.ok && data.success) {
          const durationMinutes = Number(data.data?.interview?.duration_minutes ?? data.data?.interview?.durationMinutes ?? 60)
          const apiStartMs = data.data?.session?.started_at ? new Date(data.data.session.started_at).getTime() : Date.now()
          const totalSeconds = Number.isFinite(durationMinutes) ? Math.max(1, durationMinutes) * 60 : 3600
          const serverTimers = data.data?.session?.timers || {}
          const effectiveStartMs = persisted?.sessionStartMs ?? apiStartMs
          const elapsed = Math.max(0, Math.floor((Date.now() - effectiveStartMs) / 1000))
          const persistedTimers = persisted?.stageTimers || {}
          const lastTick = persisted?.lastTickMs ? Math.floor((Date.now() - persisted.lastTickMs) / 1000) : 0
          const lastStage = persisted?.lastActiveStage as InterviewStage | undefined
          const mergedTimers = { ...(serverTimers.stageTimers || {}), ...persistedTimers }
          if (lastStage && lastTick > 0) {
            mergedTimers[lastStage] = (mergedTimers[lastStage] || 0) + lastTick
          }
          const durationSeconds = serverTimers.sessionDurationSeconds || persisted?.sessionDurationSeconds || totalSeconds
          const remainingFromServer = typeof serverTimers.remainingSeconds === 'number' ? serverTimers.remainingSeconds : null
          const computedRemaining = Math.max(0, durationSeconds - elapsed)
          const finalRemaining = remainingFromServer !== null ? remainingFromServer : computedRemaining
          setStageTimers(mergedTimers)
          setSessionDurationSeconds(durationSeconds)
          setSessionStartMs(effectiveStartMs)
          setRemainingSeconds(finalRemaining)
          setIsExpired(finalRemaining <= 0)
        } else {
          const effectiveStartMs = persisted?.sessionStartMs ?? Date.now()
          const totalSeconds = persisted?.sessionDurationSeconds ?? 3600
          const elapsed = Math.max(0, Math.floor((Date.now() - effectiveStartMs) / 1000))
          setStageTimers(persisted?.stageTimers || {})
          setSessionDurationSeconds(totalSeconds)
          setSessionStartMs(effectiveStartMs)
          setRemainingSeconds(Math.max(0, totalSeconds - elapsed))
          setIsExpired(false)
        }
      } catch (error) {
        console.error('Failed to load session metadata', error)
        setSessionDurationSeconds(3600)
        setSessionStartMs(Date.now())
        setRemainingSeconds(3600)
        setIsExpired(false)
      }
    }

    loadSessionMetadata()
  }, [sessionIdFromUrl])

  // Global countdown timer (header)
  useEffect(() => {
    if (isExpired || sessionStartMs === null) return
    const interval = setInterval(() => {
      const elapsed = Math.max(0, Math.floor((Date.now() - sessionStartMs) / 1000))
      const remaining = Math.max(0, sessionDurationSeconds - elapsed)
      setRemainingSeconds(remaining)
      persistTiming(stageTimers, { lastActiveStage: activeStage })
      if (remaining <= 0) {
        setIsExpired(true)
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [sessionStartMs, sessionDurationSeconds, isExpired, stageTimers, activeStage])

  // Stage-specific timers (pause/resume per stage)
  useEffect(() => {
    if (isExpired) return
    const interval = setInterval(() => {
      setStageTimers(prev => {
        const updated = {
          ...prev,
          [activeStage]: (prev[activeStage] || 0) + 1
        }
        persistTiming(updated, { lastActiveStage: activeStage })
        return updated
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [activeStage, isExpired])

  // Persist timers to backend every 10 seconds
  useEffect(() => {
    if (!sessionIdFromUrl || isExpired) return
    const interval = setInterval(() => {
      persistTimersToServer()
    }, 10000)
    return () => clearInterval(interval)
  }, [sessionIdFromUrl, remainingSeconds, sessionDurationSeconds, stageTimers, activeStage, isExpired])

  // Persist once when timing first loads
  useEffect(() => {
    if (sessionIdFromUrl && remainingSeconds !== null) {
      persistTimersToServer()
    }
  }, [sessionIdFromUrl, remainingSeconds])

  // Persist on page unload
  useEffect(() => {
    const handler = () => {
      persistTimersToServer()
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [sessionIdFromUrl, remainingSeconds, sessionDurationSeconds, stageTimers, activeStage, isExpired])

  // Initialize store with URL parameters and load saved data
  useEffect(() => {
    if (!isAuthenticated) return // Wait for authentication
    
    const initializeData = async () => {
      if (interviewIdFromUrl) {
        // Use sessionId from URL, fallback to a default for testing
        const sessionId = sessionIdFromUrl || `test-session-${interviewIdFromUrl}`
        store.setSessionInfo(sessionId, interviewIdFromUrl)

        // Load saved inputs BEFORE marking as loaded
        await Promise.all([
          loadSystemDesignPrompt(interviewIdFromUrl),
          loadSavedInputs(interviewIdFromUrl)
        ])
      }
      
      if (stageFromUrl) {
        // Validate stage and provide fallbacks for common invalid stages
        const validStages: InterviewStage[] = ['guidelines', 'coding', 'database-questions', 'system-design', 'whiteboard', 'solution-design', 'code-review', 'video-interview', 'pair-programming', 'completed']
        const stageParam = stageFromUrl
        let validStage: InterviewStage

        // Smart mapping for 'technical' stage based on available challenges
        if (stageParam === 'technical') {
          // Wait for challenges to be loaded before deciding
          if (!availableChallenges.loaded) {
            // Default to system-design while loading
            validStage = 'system-design'
            console.log('⏳ Challenges not loaded yet, defaulting technical to system-design')
          } else {
            // Map 'technical' based on what's actually available
            if (availableChallenges.coding) {
              validStage = 'coding'
              console.log('🎯 Mapping technical stage to coding (challenges available)')
            } else if (availableChallenges.database) {
              validStage = 'database-questions'
              console.log('🎯 Mapping technical stage to database-questions (challenges available)')
            } else {
              // If no coding/database challenges, default to system-design
              validStage = 'system-design'
              console.log('🎯 Mapping technical stage to system-design (no coding/database challenges)')
            }
          }
        } else if (validStages.includes(stageParam as InterviewStage)) {
          validStage = stageParam as InterviewStage
        } else {
          console.warn(`Invalid stage '${stageParam}', defaulting to 'coding'`)
          validStage = availableChallenges.coding ? 'coding' : 'guidelines'
        }

        store.setActiveStage(validStage)
      }
      
      // Mark data as loaded after everything is done
      setDataLoaded(true)
      
      // Debug: Check store state after loading
      setTimeout(() => {
        console.log('🔍 Store state after loading:', {
          codingData1: store.stageData.coding['1'] || 'no data',
          codingDataDefault: store.stageData.coding['default'] || 'no data',
          stageData: Object.keys(store.stageData)
        })
      }, 100)
    }
    
    initializeData()
  }, [isAuthenticated, availableChallenges.loaded])  // Run when authentication status changes or challenges are loaded

  // Helper function to load data modeling inputs
  const loadDataModelingInputs = async (interviewId: string) => {
    try {
      console.log('🔄 Loading data modeling inputs for interview:', interviewId)
      const response = await fetch(buildApiUrl(`interview-responses/public/load/data_modeling?interviewId=${interviewId}`), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })

      console.log('📡 Data modeling API response status:', response.status)
      if (response.ok) {
        const result = await response.json()
        console.log('📦 Data modeling API result:', result)
        if (result.success && result.data.hasData && result.data.response) {
          const input = result.data.response
          console.log('📥 Loaded data modeling input:', input)
          
          // Process data modeling input
          try {
            const modelDataCandidate =
              toObject(input.databaseModelingData) ||
              toObject(input.modelData) ||
              toObject(input.database_modeling_data) ||
              toObject(input.model_data) ||
              null

            const entitiesRaw = pickArray(
              toArray(input.databaseModelingEntities),
              toArray(input.entitiesData),
              toArray(modelDataCandidate?.entities),
              toArray(modelDataCandidate)
            )

            const relationshipsRaw = pickArray(
              toArray(input.databaseModelingRelationships),
              toArray(input.relationshipsData),
              toArray(modelDataCandidate?.relationships)
            )

            const foreignKeysRaw = pickArray(
              toArray(input.databaseModelingForeignKeys),
              toArray(input.foreignKeysData),
              toArray(modelDataCandidate?.fkRelationships),
              toArray(modelDataCandidate?.foreignKeys)
            )

            const commentsRaw = pickArray(
              toArray(input.databaseModelingComments),
              toArray(input.commentsData),
              toArray(modelDataCandidate?.comments)
            )

            const zoomPanState =
              toObject(input.databaseModelingZoomPan) ||
              toObject(input.zoomPanState) ||
              toObject(modelDataCandidate?.zoomPan) ||
              (modelDataCandidate
                ? {
                    zoom: modelDataCandidate.zoom,
                    panX: modelDataCandidate.panX,
                    panY: modelDataCandidate.panY
                  }
                : null)

            const textContent =
              (typeof input.content === 'string' && input.content) ||
              (modelDataCandidate && typeof modelDataCandidate.content === 'string'
                ? modelDataCandidate.content
                : '')

            const normalizedEntities = entitiesRaw.map((entity: any, index: number) => {
              const entityId = toOptionalString(
                entity?.id ?? entity?.uuid ?? entity?.key ?? entity?.identifier ?? entity?.entityId,
                `entity-${index}`
              )!

              const attributeCandidates = pickArray(
                toArray(entity?.attributes),
                toArray(entity?.fields),
                toArray(entity?.columns)
              )

              const normalizedAttributes = attributeCandidates.map((attr: any, attrIndex: number) => {
                const attributeId = toOptionalString(
                  attr?.id ?? attr?.uuid ?? attr?.key ?? attr?.identifier ?? attr?.attributeId ?? attr?.columnId ?? attr?.name,
                  `${entityId}-attr-${attrIndex}`
                )!

                return {
                  id: attributeId,
                  name: toOptionalString(attr?.name ?? attr?.label ?? attr?.title ?? attr?.key, `field_${attrIndex + 1}`)!,
                  type: toOptionalString(attr?.type ?? attr?.dataType ?? attr?.fieldType ?? attr?.columnType, 'TEXT')!,
                  isPrimary: parseBoolean(attr?.isPrimary ?? attr?.primary ?? attr?.is_primary ?? attr?.primaryKey ?? attr?.isPrimaryKey ?? attr?.pk, false),
                  isRequired: parseBoolean(attr?.isRequired ?? attr?.required ?? attr?.is_required ?? attr?.notNull ?? attr?.mandatory, false),
                  isForeign: parseBoolean(attr?.isForeign ?? attr?.foreign ?? attr?.is_foreign ?? attr?.foreignKey ?? attr?.fk, false),
                  defaultValue: attr?.defaultValue ?? attr?.default ?? attr?.default_value ?? null
                }
              })

              return {
                id: entityId,
                name: toOptionalString(entity?.name ?? entity?.title ?? entity?.label, `Entity ${index + 1}`)!,
                x: parseNumber(entity?.x ?? entity?.position?.x ?? entity?.posX ?? entity?.left ?? entity?.layout?.x, 120) ?? 120,
                y: parseNumber(entity?.y ?? entity?.position?.y ?? entity?.posY ?? entity?.top ?? entity?.layout?.y, 80) ?? 80,
                fields: normalizedAttributes
              }
            })

            const normalizedRelationships = relationshipsRaw
              .map((rel: any, relIndex: number) => {
                const fromEntityId = toOptionalString(
                  rel?.fromEntityId ?? rel?.sourceEntityId ?? rel?.source?.entityId ?? rel?.fromEntity ?? rel?.entityA ?? rel?.parentEntityId
                )
                const toEntityId = toOptionalString(
                  rel?.toEntityId ?? rel?.targetEntityId ?? rel?.target?.entityId ?? rel?.toEntity ?? rel?.entityB ?? rel?.childEntityId
                )

                if (!fromEntityId || !toEntityId) {
                  return null
                }

                const typeValue = toOptionalString(rel?.type ?? rel?.relationshipType ?? rel?.kind ?? rel?.cardinality, 'one-to-many')

                return {
                  id: toOptionalString(rel?.id ?? rel?.uuid ?? rel?.key ?? rel?.identifier, `relationship-${relIndex}`)!,
                  fromEntityId,
                  toEntityId,
                  type: (typeValue === 'many-to-one' || typeValue === 'one-to-one' || typeValue === 'many-to-many')
                    ? typeValue
                    : 'one-to-many',
                  fromField: toOptionalString(rel?.fromField ?? rel?.sourceFieldId ?? rel?.source?.fieldId ?? rel?.fromFieldId ?? rel?.sourceField) || '',
                  toField: toOptionalString(rel?.toField ?? rel?.targetFieldId ?? rel?.target?.fieldId ?? rel?.toFieldId ?? rel?.targetField) || ''
                }
              })
              .filter((rel): rel is {
                id: string
                fromEntityId: string
                toEntityId: string
                type: 'one-to-one' | 'one-to-many' | 'many-to-many'
                fromField?: string
                toField?: string
              } => Boolean(rel))

            const normalizedForeignKeys = foreignKeysRaw
              .map((fk: any, fkIndex: number) => {
                const fromEntityId = toOptionalString(fk?.fromEntityId ?? fk?.sourceEntityId ?? fk?.entityA ?? fk?.parentEntityId)
                const toEntityId = toOptionalString(fk?.toEntityId ?? fk?.targetEntityId ?? fk?.entityB ?? fk?.childEntityId)

                if (!fromEntityId || !toEntityId) {
                  return null
                }

                return {
                  id: toOptionalString(fk?.id ?? fk?.uuid ?? fk?.key ?? fk?.identifier, `fk-${fkIndex}`)!,
                  fromEntityId,
                  toEntityId,
                  type: 'one-to-many' as const,
                  fromField: toOptionalString(fk?.fromField ?? fk?.sourceFieldId ?? fk?.field ?? fk?.fromColumn ?? fk?.sourceField) || '',
                  toField: toOptionalString(fk?.toField ?? fk?.targetFieldId ?? fk?.targetColumn ?? fk?.targetField) || ''
                }
              })
              .filter((fk): fk is {
                id: string
                fromEntityId: string
                toEntityId: string
                type: 'one-to-many'
                fromField?: string
                toField?: string
              } => Boolean(fk))

            const normalizedComments = commentsRaw.map((comment: any, commentIndex: number) => ({
              id: toOptionalString(comment?.id ?? comment?.uuid ?? comment?.key ?? comment?.identifier, `comment-${commentIndex}`)!,
              text: toOptionalString(comment?.text ?? comment?.content ?? comment?.note ?? comment?.label, '') || '',
              x: parseNumber(comment?.x ?? comment?.position?.x ?? comment?.left, 0) ?? 0,
              y: parseNumber(comment?.y ?? comment?.position?.y ?? comment?.top, 0) ?? 0,
              color: toOptionalString(comment?.color ?? comment?.accent ?? '#2563eb', '#2563eb')!
            }))

            const zoom = parseNumber(zoomPanState?.zoom, 1) ?? 1
            const panX = parseNumber(zoomPanState?.panX, 0) ?? 0
            const panY = parseNumber(zoomPanState?.panY, 0) ?? 0

            console.log('🔍 Data modeling parsed data:', {
              entitiesCount: normalizedEntities.length,
              relationshipsCount: normalizedRelationships.length,
              foreignKeysCount: normalizedForeignKeys.length,
              commentsCount: normalizedComments.length,
              zoom,
              panX,
              panY
            })

            store.updateDataModelingContent(textContent || '')
            store.updateDataModelingEntities(normalizedEntities as any)
            store.updateDataModelingRelationships(normalizedRelationships as any)
            store.updateDataModelingFKRelationships(normalizedForeignKeys as any)
            store.updateDataModelingComments(normalizedComments as any)
            store.updateDataModelingZoom(zoom)
            store.updateDataModelingPan(panX, panY)

            store.markStageClean('data-modeling')
          } catch (e) {
            console.warn('⚠️ Failed to parse data modeling data:', e)
          }
        }
      } else {
        console.log('ℹ️ No data modeling data found')
      }
    } catch (error) {
      console.warn('⚠️ Error loading data modeling inputs:', error)
    }
  }

  // Helper function to load system design inputs
  const loadSystemDesignInputs = async (interviewId: string) => {
    try {
      const response = await fetch(buildApiUrl(`interview-responses/public/load/system_design?interviewId=${interviewId}`), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        console.log('ℹ️ No system design data found')
        return
      }

      const result = await response.json()
      if (!result.success || !result.data.hasData || !result.data.response) {
        console.log('ℹ️ System design response empty')
        return
      }

      const input = result.data.response
      console.log('📥 Loaded system design input:', input)

      try {
        const diagramCandidates = [
          input.systemDesignWhiteboardData,
          input.system_design_whiteboard_data,
          input.diagramElements,
          input.diagram_elements,
          input.whiteboardData,
          input.diagram
        ]

        let parsedDiagram: Record<string, any> | any[] | null = null
        for (const candidate of diagramCandidates) {
          const objectCandidate = toObject(candidate)
          if (objectCandidate) {
            parsedDiagram = objectCandidate
            break
          }
          const arrayCandidate = toArray(candidate)
          if (arrayCandidate) {
            parsedDiagram = arrayCandidate
            break
          }
        }

        const diagramObject = parsedDiagram && !Array.isArray(parsedDiagram) ? parsedDiagram : null

        const componentsRaw = pickArray(
          diagramObject?.placedComponents,
          diagramObject?.components,
          Array.isArray(parsedDiagram) ? (parsedDiagram as any[]) : undefined,
          toArray(input.placedComponents),
          toArray(input.components)
        )

        const connectionsRaw = pickArray(
          diagramObject?.connections,
          diagramObject?.edges,
          diagramObject?.links,
          Array.isArray(parsedDiagram)
            ? (parsedDiagram as any[]).filter((item: any) => item && (item.fromComponentId || item.toComponentId))
            : undefined,
          toArray(input.connections)
        )

        const normalizedComponents = componentsRaw.map((component: any, index: number) => {
          const guessedId = toOptionalString(component?.id ?? component?.uuid ?? component?.key ?? component?.identifier, `component-${index}`)!
          const width = parseNumber(component?.width ?? component?.size?.width, 180) || 180
          const height = parseNumber(component?.height ?? component?.size?.height, 120) || 120
          const defaultX = 100 + (index % 4) * 220
          const defaultY = 100 + Math.floor(index / 4) * 180

          return {
            id: guessedId,
            componentId: toOptionalString(
              component?.componentId ?? component?.component_id ?? component?.type ?? component?.templateId ?? guessedId,
              guessedId
            )!,
            name: toOptionalString(component?.name ?? component?.title ?? component?.label, `Component ${index + 1}`)!,
            icon: toOptionalString(component?.icon ?? component?.iconName ?? component?.icon_id ?? component?.componentId ?? 'Server', 'Server')!,
            color: toOptionalString(component?.color ?? component?.accent ?? component?.theme ?? 'bg-blue-500', 'bg-blue-500')!,
            x: parseNumber(component?.x ?? component?.position?.x ?? component?.left ?? component?.posX, defaultX) ?? defaultX,
            y: parseNumber(component?.y ?? component?.position?.y ?? component?.top ?? component?.posY, defaultY) ?? defaultY,
            width,
            height
          }
        })

        const validComponentIds = new Set(normalizedComponents.map(component => component.id))

        const normalizedConnections = connectionsRaw
          .map((connection: any, index: number) => {
            const fromId = toOptionalString(
              connection?.fromComponentId ?? connection?.from_component_id ?? connection?.sourceComponentId ?? connection?.source?.componentId ?? connection?.from ?? connection?.sourceId
            )
            const toId = toOptionalString(
              connection?.toComponentId ?? connection?.to_component_id ?? connection?.targetComponentId ?? connection?.target?.componentId ?? connection?.to ?? connection?.targetId
            )

            if (!fromId || !toId) {
              return null
            }

            const normalizedType = (() => {
              const rawType = toOptionalString(connection?.type ?? connection?.style ?? 'arrow', 'arrow')!.toLowerCase()
              return rawType.includes('line') ? 'line' : 'arrow'
            })()

            return {
              id: toOptionalString(connection?.id ?? connection?.uuid ?? connection?.key, `connection-${index}`)!,
              fromComponentId: fromId,
              toComponentId: toId,
              color: toOptionalString(connection?.color ?? connection?.stroke ?? '#374151', '#374151')!,
              strokeWidth: parseNumber(connection?.strokeWidth ?? connection?.width ?? connection?.thickness, 2) ?? 2,
              type: normalizedType as 'arrow' | 'line',
              label: toOptionalString(connection?.label ?? connection?.name ?? connection?.description)
            }
          })
          .filter((connection): connection is {
            id: string
            fromComponentId: string
            toComponentId: string
            color: string
            strokeWidth: number
            type: 'arrow' | 'line'
            label?: string
          } => Boolean(connection && validComponentIds.has(connection.fromComponentId) && validComponentIds.has(connection.toComponentId)))

        const designMode = toOptionalString(
          diagramObject?.designMode ?? input.designMode ?? input.mode,
          'visual'
        )

        const selectedTool = toOptionalString(
          diagramObject?.selectedTool ?? input.selectedTool ?? input.tool,
          'move'
        )

        const textContent =
          (typeof input.content === 'string' && input.content) ||
          (typeof input.designNotes === 'string' && input.designNotes) ||
          (typeof input.design_notes === 'string' && input.design_notes) ||
          (typeof input.systemDesignDiscussion === 'string' && input.systemDesignDiscussion) ||
          (typeof input.system_design_discussion === 'string' && input.system_design_discussion) ||
          (diagramObject && typeof diagramObject.designNotes === 'string' ? diagramObject.designNotes : '')

        store.updateSystemDesignContent(textContent || '')
        store.updateSystemDesignComponents(normalizedComponents)
        store.updateSystemDesignConnections(normalizedConnections)

        if (designMode === 'visual' || designMode === 'text') {
          store.setSystemDesignMode(designMode)
        }

        if (selectedTool === 'move' || selectedTool === 'connect' || selectedTool === 'delete') {
          store.setSystemDesignTool(selectedTool)
        }

        store.markStageClean('system-design')
      } catch (e) {
        console.warn('⚠️ Failed to parse system design data:', e)
      }
    } catch (error) {
      console.warn('⚠️ Error loading system design inputs:', error)
    }
  }

  const loadSystemDesignPrompt = async (interviewId: string) => {
    try {
      const response = await fetch(buildApiUrl(`interviews/${interviewId}/system-design/prompt`), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        console.warn('⚠️ Failed to load system design prompt:', response.statusText)
        store.setSystemDesignPrompt(null)
        return
      }

      const result = await response.json()
      if (result.success && Array.isArray(result.data) && result.data.length > 0) {
        const meaningfulPrompt = result.data.find((row: any) => {
          const notes = typeof row?.notes === 'string' ? row.notes.trim() : ''
          const parsed = row?.parsed_notes || row?.parsedNotes
          return (notes && notes.length > 0) || (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0)
        }) || result.data[0]

        store.setSystemDesignPrompt(normalizeSystemDesignPrompt(meaningfulPrompt))
      } else {
        store.setSystemDesignPrompt(null)
      }
    } catch (error) {
      console.warn('⚠️ Error loading system design prompt:', error)
      store.setSystemDesignPrompt(null)
    }
  }

  // Helper function to load UML design inputs
  const loadUMLDesignInputs = async (interviewId: string) => {
    try {
      const response = await fetch(buildApiUrl(`interview-responses/public/load/uml_design?interviewId=${interviewId}`), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })

      if (response.ok) {
        const result = await response.json()
        if (result.success && result.data.hasData && result.data.response) {
          const input = result.data.response
          console.log('📥 Loaded UML design input:', input)
          
          // Process UML design input
          try {
            const classes =
              toArray(input.classesData) ||
              toArray(input.classes_data) ||
              []

            const relationships =
              toArray(input.relationshipsData) ||
              toArray(input.relationships_data) ||
              []

            const diagramType =
              (typeof input.diagramType === 'string' && input.diagramType) ||
              (typeof input.diagram_type === 'string' && input.diagram_type)

            store.updateUMLContent(typeof input.content === 'string' ? input.content : '')

            if (classes.length > 0) {
              store.updateUMLClasses(classes)
            }

            if (relationships.length > 0) {
              store.updateUMLRelationships(relationships)
            }

            if (diagramType === 'class' || diagramType === 'sequence' || diagramType === 'usecase') {
              store.setUMLDiagramType(diagramType)
            }

            store.markStageClean('uml')
          } catch (e) {
            console.warn('⚠️ Failed to parse UML design data:', e)
          }
        }
      } else {
        console.log('ℹ️ No UML design data found')
      }
    } catch (error) {
      console.warn('⚠️ Error loading UML design inputs:', error)
    }
  }

  // Helper function to load whiteboard inputs
  const loadWhiteboardInputs = async (interviewId: string) => {
    try {
      console.log('🔄 Loading whiteboard inputs for interview:', interviewId)
      const response = await fetch(buildApiUrl(`interview-responses/public/load/whiteboard?interviewId=${interviewId}`), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })

      console.log('📡 Whiteboard API response status:', response.status)
      if (response.ok) {
        const result = await response.json()
        console.log('📦 Whiteboard API result:', result)
        if (result.success && result.data.hasData && result.data.response) {
          const input = result.data.response
          console.log('📥 Loaded whiteboard input:', input)
          
          // Process whiteboard input
          try {
            const canvasElements =
              toObject(input.canvasElements) ||
              toObject(input.canvas_elements) ||
              null

            const canvasSize =
              toObject(input.canvasSize) ||
              toObject(input.canvas_size) ||
              null

            console.log('🔍 Processing whiteboard data:', {
              hasContent: !!input.content,
              hasCanvasElements: !!canvasElements,
              hasCanvasSize: !!canvasSize
            })
            
            const resolvedContent =
              (typeof input.content === 'string' && input.content) ||
              (typeof input.whiteboardNotes === 'string' && input.whiteboardNotes) ||
              (typeof input.whiteboard_notes === 'string' && input.whiteboard_notes) ||
              (typeof input.whiteboardTextDiscussion === 'string' && input.whiteboardTextDiscussion) ||
              (typeof input.whiteboard_text_discussion === 'string' && input.whiteboard_text_discussion) ||
              ''

            if (resolvedContent) {
              console.log('✅ Updating whiteboard content:', resolvedContent.substring(0, 50) + '...')
            } else {
              console.log('ℹ️ Whiteboard content empty, resetting state')
            }
            store.updateWhiteboardContent(resolvedContent)
            
            const canvasData =
              (canvasElements && typeof canvasElements.canvasData === 'string' && canvasElements.canvasData) ||
              (canvasElements && typeof canvasElements.canvas_data === 'string' && canvasElements.canvas_data) ||
              (typeof input.canvasData === 'string' ? input.canvasData : null) ||
              (typeof input.canvas_data === 'string' ? input.canvas_data : null) ||
              (input.strokeData && typeof input.strokeData.canvasData === 'string' ? input.strokeData.canvasData : null) ||
              (input.stroke_data && typeof input.stroke_data.canvasData === 'string' ? input.stroke_data.canvasData : null)

            if (canvasData) {
              console.log('✅ Updating whiteboard canvas data, length:', canvasData.length)
              store.updateWhiteboardCanvas(canvasData)
            } else {
              console.log('ℹ️ No canvas data to load')
              store.updateWhiteboardCanvas('')
            }

            const resolvedTool = toOptionalString(
              canvasSize?.selectedTool ??
              canvasSize?.selected_tool ??
              canvasElements?.selectedTool ??
              canvasElements?.selected_tool ??
              input.selectedTool ??
              input.selected_tool,
              'pen'
            )

            const resolvedColor = toOptionalString(
              canvasSize?.selectedColor ??
              canvasSize?.selected_color ??
              canvasElements?.selectedColor ??
              canvasElements?.selected_color ??
              input.selectedColor ??
              input.selected_color,
              '#000000'
            )

            const strokeWidthValue = (() => {
              const candidate =
                canvasSize?.strokeWidth ??
                canvasSize?.stroke_width ??
                canvasElements?.strokeWidth ??
                canvasElements?.stroke_width ??
                input.strokeWidth ??
                input.stroke_width
              if (typeof candidate === 'number') return candidate
              if (typeof candidate === 'string') {
                const parsed = Number(candidate)
                return Number.isNaN(parsed) ? undefined : parsed
              }
              return undefined
            })()

            const drawingMode = toOptionalString(
              canvasElements?.drawingMode ??
              canvasElements?.drawing_mode ??
              canvasSize?.drawingMode ??
              canvasSize?.drawing_mode ??
              input.drawingMode ??
              input.drawing_mode,
              'draw'
            )

            if (resolvedTool) {
              store.setWhiteboardTool(resolvedTool)
            }
            if (resolvedColor) {
              store.setWhiteboardColor(resolvedColor)
            }
            if (typeof strokeWidthValue === 'number' && !Number.isNaN(strokeWidthValue)) {
              store.setWhiteboardStrokeWidth(strokeWidthValue)
            }
            if (drawingMode === 'draw' || drawingMode === 'text') {
              store.setWhiteboardMode(drawingMode)
            }

            store.markStageClean('whiteboard')
          } catch (e) {
            console.warn('⚠️ Failed to parse whiteboard data:', e)
          }
        }
      } else {
        console.log('ℹ️ No whiteboard data found')
      }
    } catch (error) {
      console.warn('⚠️ Error loading whiteboard inputs:', error)
    }
  }

  // Function to load saved inputs from API
  const loadSavedInputs = async (interviewId: string) => {
    try {
      console.log('🔄 Loading saved inputs for interview:', interviewId)
      
      // Load inputs from dedicated consolidated endpoints
      await Promise.all([
        loadDataModelingInputs(interviewId),
        loadSystemDesignInputs(interviewId),
        loadUMLDesignInputs(interviewId),
        loadWhiteboardInputs(interviewId)
      ])
      
      console.log('✅ Successfully loaded all interview response inputs')
    } catch (error) {
      console.error('❌ Error loading saved inputs:', error)
    }
  }

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = seconds % 60
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const getStageIcon = (stage: InterviewStage) => {
    switch (stage) {
      case 'guidelines': return <FileText className="w-5 h-5" />
      case 'coding': return <Code2 className="w-5 h-5" />
      case 'database-questions': return <Database className="w-5 h-5" />
      case 'system-design': return <Layers className="w-5 h-5" />
      case 'data-modeling': return <Table className="w-5 h-5" />
      case 'uml': return <Boxes className="w-5 h-5" />
      case 'whiteboard': return <Palette className="w-5 h-5" />
      case 'notes': return <StickyNote className="w-5 h-5" />
      default: return <Clock className="w-5 h-5" />
    }
  }

  const stages = [
    { id: 'guidelines', name: 'Guidelines', description: 'Review interview guidelines' },
    { id: 'coding', name: 'Coding Challenge', description: 'Solve algorithmic problems' },
    { id: 'database-questions', name: 'Database Challenge', description: 'Solve database problems' },
    { id: 'system-design', name: 'System Design', description: 'Design scalable systems' },
    { id: 'data-modeling', name: 'Data Modeling', description: 'Create entity relationship diagrams' },
    { id: 'uml', name: 'UML Design', description: 'Design UML class and use case diagrams' },
    { id: 'whiteboard', name: 'Whiteboard Discussion', description: 'Design and discuss solutions' },
    { id: 'notes', name: 'Notes', description: 'Take interview notes and observations' }
  ]

  // Show loading state while checking authentication
  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    )
  }

  // Don't render anything if not authenticated (will redirect to login)
  if (!isAuthenticated) {
    return null
  }

  // Handle guidelines stage early return - show immediately (no data loading needed)
  if (activeStage === 'guidelines') {
    return (
      <div className="max-w-4xl mx-auto space-y-6 p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Interview Guidelines</h2>
          <p className="text-gray-600">Please read these guidelines carefully</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Instructions</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>✓ Use any resources you would normally use at work</li>
              <li>✓ Code will automatically save every 5 seconds</li>
              <li>✓ Switch between problems and stages freely</li>
              <li>✓ Your progress is preserved when navigating</li>
            </ul>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="guidelines-read"
              checked={interviewConfig.guidelinesRead}
              onChange={(e) => setInterviewConfig(prev => ({ ...prev, guidelinesRead: e.target.checked }))}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="guidelines-read" className="text-sm text-gray-700">
              I have read and understood the guidelines
            </label>
          </div>
          <button
            onClick={() => switchStage('coding')}
            disabled={!interviewConfig.guidelinesRead}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            data-testid="start-interview"
          >
            Start Interview
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 text-sm text-gray-600">
            <Clock className="w-4 h-4" />
            <span>Section time: {formatTime(stageTimers[activeStage] || 0)}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Clock className="w-4 h-4" />
            <span>{formatTime(Math.max(0, remainingSeconds ?? sessionDurationSeconds))}</span>
          </div>
          <button
            onClick={() => setIsNotepadOpen(!isNotepadOpen)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
              isNotepadOpen
                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Toggle Notes"
          >
            <StickyNote className="w-4 h-4" />
            <span className="text-sm font-medium">Notes</span>
          </button>
        </div>
      </div>

      <div className="flex h-screen relative">
        {isExpired && (
          <div className="absolute inset-0 z-20 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center text-center p-6">
            <div className="text-xl font-semibold text-gray-900 mb-2">Time is up</div>
            <p className="text-gray-700 mb-4">Interview time has ended. Editing is disabled.</p>
          </div>
        )}
        {/* Sidebar */}
        <div className={`${isSidebarCollapsed ? 'w-12' : 'w-64'} bg-white border-r border-gray-200 overflow-y-auto transition-all duration-300`}>
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              {!isSidebarCollapsed && (
                <h2 className="text-sm font-semibold text-gray-900">Interview Stages</h2>
              )}
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              >
                {isSidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
              </button>
            </div>
            
            <nav className="space-y-2">
              {stages.slice(1).map((stage) => (
                <button
                  key={stage.id}
                  onClick={() => !isExpired && switchStage(stage.id as InterviewStage)}
                  className={`w-full flex items-center ${isSidebarCollapsed ? 'justify-center px-2' : 'space-x-3 px-3'} py-2 text-left text-sm rounded-md transition-colors ${
                    activeStage === stage.id
                      ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  disabled={isExpired}
                  data-testid={`nav-${stage.id}`}
                >
                  {getStageIcon(stage.id as InterviewStage)}
                  {!isSidebarCollapsed && (
                    <div>
                      <div className="font-medium">{stage.name}</div>
                      <div className="text-xs text-gray-500">{stage.description}</div>
                    </div>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1">
            {!dataLoaded ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-gray-600">Loading saved data...</p>
                </div>
              </div>
            ) : (
              <>
                {activeStage === 'coding' && <CodingChallenge />}
                {activeStage === 'database-questions' && <DatabaseQuestions />}
                {activeStage === 'system-design' && <SystemDesign />}
                {activeStage === 'data-modeling' && <DataModeling />}
                {activeStage === 'uml' && <UMLDesign />}
                {activeStage === 'whiteboard' && <WhiteboardDiscussion />}
                {activeStage === 'notes' && <NotesStage />}
                {activeStage === 'completed' && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">Interview Completed!</h2>
                      <p className="text-gray-600">Thank you for participating.</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Notepad Component */}
      <Notepad
        isOpen={isNotepadOpen}
        onToggle={() => setIsNotepadOpen(!isNotepadOpen)}
        sessionId={sessionIdFromUrl || ''}
      />
    </div>
  )
}

// Main export with Suspense wrapper
export default function InterviewPage() {
  return (
    <Suspense fallback={<InterviewLoading />}>
      <InterviewContent />
    </Suspense>
  )
}
